import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function fmtDur(mins) {
  if (!mins && mins !== 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function diffMins(a, b) {
  return Math.round((new Date(b) - new Date(a)) / 60000);
}

// Distinguishes how a clock-out actually happened, based on the note the server attaches:
// self-service (no note), the daily auto-close job (forgot to clock out), a manual entry by
// the owner/admin, or a status correction made from the Attendance Register grid.
function clockOutTypeLabel(notes) {
  if (!notes) return null;
  if (notes.includes('Auto-closed')) return 'system auto clock-out';
  if (notes.includes('corrected by')) return 'corrected by owner';
  if (notes.includes('Manual entry')) return notes.match(/Manual entry by (.+)/)?.[0]?.replace('Manual entry by', 'manual entry by') || 'manual entry';
  return null;
}

const BREAK_LABELS = { lunch: 'Lunch Break', tea: 'Tea Break', casual: 'Casual Break' };
const BREAK_COLOR  = { lunch: '#FEF3C7', tea: '#FEF9C3', casual: '#FEE2E2' };
const BREAK_TEXT   = { lunch: '#92400E', tea: '#854D0E', casual: '#991B1B' };

export default function TimelinePage() {
  const { user, isOwner } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate]     = useState(today);
  const [userId, setUserId] = useState(user?.id || '');

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r => r.data),
    enabled: !!isOwner,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['timeline', userId, date],
    queryFn: () => api.get('/attendance-sessions/timeline', { params: { userId, date } }).then(r => r.data),
    enabled: !!userId && !!date,
  });

  const session = data?.session || null;
  const breaks  = data?.breaks  || [];
  const entries = data?.entries || [];
  const userName = data?.userName || '';

  // Build timeline events
  const events = [];
  if (session?.clock_in)  events.push({ type: 'clock_in',  time: session.clock_in });
  breaks.forEach(b => {
    events.push({ type: 'break_start', time: b.started_at, break_type: b.break_type, ended_at: b.ended_at });
    if (b.ended_at) events.push({ type: 'break_end', time: b.ended_at, break_type: b.break_type, started_at: b.started_at });
  });
  if (session?.clock_out) events.push({ type: 'clock_out', time: session.clock_out });
  events.sort((a, b) => new Date(a.time) - new Date(b.time));

  // Timeline bar: compute % positions
  const barStart = session?.clock_in  ? new Date(session.clock_in)  : null;
  const barEnd   = session?.clock_out ? new Date(session.clock_out) : (barStart ? new Date(barStart.getTime() + (session?.total_minutes || 480) * 60000) : null);
  const totalMs  = barStart && barEnd ? barEnd - barStart : 0;

  function pct(ts) {
    if (!totalMs || !ts) return 0;
    return Math.min(100, Math.max(0, ((new Date(ts) - barStart) / totalMs) * 100));
  }

  const totalWorked = session?.total_minutes
    ? session.total_minutes
    : session?.clock_in && !session?.clock_out
      ? diffMins(session.clock_in, new Date())
      : 0;
  const totalBreak = breaks.reduce((s, b) => {
    if (!b.ended_at) return s;
    return s + diffMins(b.started_at, b.ended_at);
  }, 0);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 0 40px' }}>
      <div className="section-head"><h2>Daily Timeline</h2></div>

      {/* Controls */}
      <div className="card" style={{ marginBottom: 16, padding: '14px 16px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          {isOwner && (
            <select value={userId} onChange={e => setUserId(e.target.value)} style={{ minWidth: 160 }}>
              <option value={user.id}>My timeline</option>
              {users.filter(u => u.id !== user.id && u.role !== 'owner').map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          )}
          <input type="date" value={date} onChange={e => setDate(e.target.value)} max={today} />
          <button className="btn-sm" onClick={() => setDate(today)}>Today</button>
          <button className="btn-sm" onClick={() => {
            const d = new Date(date); d.setDate(d.getDate() - 1); setDate(d.toISOString().slice(0, 10));
          }}>← Prev</button>
          <button className="btn-sm" onClick={() => {
            const d = new Date(date); d.setDate(d.getDate() + 1);
            if (d.toISOString().slice(0, 10) <= today) setDate(d.toISOString().slice(0, 10));
          }}>Next →</button>
        </div>
      </div>

      {isLoading && <div className="empty">Loading…</div>}

      {!isLoading && !session && (
        <div className="card"><div className="empty">No attendance record for this day.</div></div>
      )}

      {!isLoading && session && (
        <>
          {/* Summary strip */}
          <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
              {isOwner && userId !== user.id ? userName : 'My day'} · {new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <Stat label="Clock In"  value={fmtTime(session.clock_in)} />
              <Stat label="Clock Out" value={session.clock_out ? fmtTime(session.clock_out) : '—'} />
              <Stat label="Total worked" value={fmtDur(totalWorked)} accent />
              <Stat label="Break time"  value={fmtDur(totalBreak)} />
              {session.is_late && <Stat label="Late by" value={fmtDur(session.late_minutes)} warn />}
            </div>
          </div>

          {/* Visual bar */}
          {totalMs > 0 && (
            <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)', marginBottom: 12 }}>Day at a glance</div>
              <div style={{ position: 'relative', height: 28, borderRadius: 6, background: '#DCFCE7', overflow: 'visible', marginBottom: 8 }}>
                {/* Break segments */}
                {breaks.map((b, i) => {
                  if (!b.ended_at) return null;
                  const left = pct(b.started_at);
                  const width = pct(b.ended_at) - left;
                  return (
                    <div key={i} title={`${BREAK_LABELS[b.break_type] || 'Break'} · ${diffMins(b.started_at, b.ended_at)}m`}
                      style={{ position: 'absolute', top: 0, bottom: 0, left: `${left}%`, width: `${width}%`, background: BREAK_COLOR[b.break_type] || '#FEF3C7', borderRadius: 4 }} />
                  );
                })}
                {/* Active break (no end) */}
                {breaks.filter(b => !b.ended_at).map((b, i) => (
                  <div key={'act' + i} style={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(b.started_at)}%`, right: 0, background: '#FEF3C7', borderRadius: '0 6px 6px 0', opacity: 0.7 }} />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)' }}>
                <span>{fmtTime(session.clock_in)}</span>
                <span style={{ display: 'flex', gap: 16 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#DCFCE7', border: '1px solid #16A34A', display: 'inline-block' }}/> Working</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#FEF3C7', border: '1px solid #D97706', display: 'inline-block' }}/> Break</span>
                </span>
                <span>{session.clock_out ? fmtTime(session.clock_out) : 'Ongoing'}</span>
              </div>
            </div>
          )}

          {/* Event timeline */}
          <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)', marginBottom: 14 }}>Timeline</div>
            <div style={{ position: 'relative', paddingLeft: 28 }}>
              {/* Vertical line */}
              <div style={{ position: 'absolute', left: 8, top: 6, bottom: 6, width: 2, background: 'var(--border)', borderRadius: 1 }} />
              {events.map((ev, i) => {
                let dot = '#16A34A', label = '', sub = '', bg = 'transparent';
                if (ev.type === 'clock_in')    { dot = '#16A34A'; label = 'Clocked in'; }
                if (ev.type === 'clock_out')   {
                  dot = '#0369A1';
                  const typeLabel = clockOutTypeLabel(session.notes);
                  label = typeLabel ? `Clocked out (${typeLabel})` : 'Clocked out';
                  sub = `Total: ${fmtDur(totalWorked)}`;
                }
                if (ev.type === 'break_start') {
                  dot = '#D97706';
                  label = BREAK_LABELS[ev.break_type] || 'Break started';
                  bg = BREAK_COLOR[ev.break_type] || '#FEF3C7';
                }
                if (ev.type === 'break_end') {
                  dot = '#D97706';
                  label = `${BREAK_LABELS[ev.break_type] || 'Break'} ended`;
                  sub = `Duration: ${fmtDur(diffMins(ev.started_at, ev.time))}`;
                  bg = BREAK_COLOR[ev.break_type] || '#FEF3C7';
                }
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14, position: 'relative' }}>
                    <div style={{ position: 'absolute', left: -24, top: 4, width: 10, height: 10, borderRadius: '50%', background: dot, border: '2px solid #fff', boxShadow: `0 0 0 2px ${dot}`, zIndex: 1 }} />
                    <div style={{ minWidth: 70, fontSize: 12, color: 'var(--text2)', fontWeight: 600, paddingTop: 2, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(ev.time)}</div>
                    <div style={{ flex: 1, padding: sub || bg !== 'transparent' ? '6px 10px' : '2px 0', borderRadius: 6, background: bg }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: ev.type.startsWith('break') ? (BREAK_TEXT[ev.break_type] || '#92400E') : 'var(--text)' }}>{label}</div>
                      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
                    </div>
                  </div>
                );
              })}
              {events.length === 0 && <div className="empty">No events recorded.</div>}
            </div>
          </div>

          {/* Work entries */}
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)', marginBottom: 12 }}>Work logged this day</div>
            {entries.length === 0 && <div className="empty">No work entries for this day.</div>}
            {entries.map((e, i) => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < entries.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: 'var(--accent)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{e.description}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                    <span className="pill pill-blue" style={{ fontSize: 10 }}>{e.client_name}</span>
                    {' · '}
                    <span className="pill pill-gray" style={{ fontSize: 10 }}>{e.category_name}</span>
                  </div>
                </div>
                {e.quantity > 1 && <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>×{e.quantity}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent ? 'var(--accent)' : warn ? '#D97706' : 'var(--text)' }}>{value}</div>
    </div>
  );
}
